import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { BusinessIcon } from '../../icons';
import { Camera, Youtube } from 'lucide-react';
import { TwitterLogo, FacebookLogo } from 'phosphor-react';

interface SocialMediaStepProps {
  uid: string;
  onNext: () => void;
  onBack: () => void;
}

interface SocialMediaConnection {
  platform: string;
  connected: boolean;
  username?: string;
  accessToken?: string;
  refreshToken?: string;
  lastSync?: Date;
}

interface SocialSharingSettings {
  enabled: boolean;
  frequency: 'off' | '3x' | '5x' | '7x';
  platforms: string[];
  autoPost: boolean;
  templates: {
    linkedin?: string;
    twitter?: string;
    instagram?: string;
  };
}

const SOCIAL_PLATFORMS: { id: string; name: string; icon: React.ReactNode; description: string; color: string; benefits: string[] }[] = [
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: <BusinessIcon size={22} weight="regular" className="text-white" />,
    description: 'Professioneel netwerk en B2B content',
    color: 'bg-blue-600',
    benefits: ['Netwerk uitbreiden', 'B2B leads', 'Thought leadership']
  },
  {
    id: 'twitter',
    name: 'Twitter/X',
    icon: <TwitterLogo size={22} weight="regular" className="text-white" />,
    description: 'Real-time updates en engagement',
    color: 'bg-black',
    benefits: ['Real-time updates', 'Quick engagement', 'Trending topics']
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: <Camera size={22} className="text-white" />,
    description: 'Visuele content en storytelling',
    color: 'bg-gradient-to-r from-purple-500 to-pink-500',
    benefits: ['Visuele storytelling', 'Brand awareness', 'Community building']
  },
  {
    id: 'youtube',
    name: 'YouTube',
    icon: <Youtube size={22} className="text-white" />,
    description: 'Video content en long-form',
    color: 'bg-red-600',
    benefits: ['Video content', 'SEO benefits', 'Long-form content']
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: <FacebookLogo size={22} weight="regular" className="text-white" />,
    description: 'Community en events',
    color: 'bg-blue-700',
    benefits: ['Community building', 'Event promotion', 'Local reach']
  }
];

export default function SocialMediaStep({ uid, onNext, onBack }: SocialMediaStepProps) {
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState<SocialMediaConnection[]>([]);
  const [sharingSettings, setSharingSettings] = useState<SocialSharingSettings>({
    enabled: false,
    frequency: '3x',
    platforms: [],
    autoPost: false,
    templates: {}
  });

  useEffect(() => {
    loadExistingData();
  }, [uid]);

  const loadExistingData = async () => {
    try {
      const profileRef = doc(db, 'userProfiles', uid);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        const data = profileSnap.data();
        setConnections(data.socialMediaConnections || []);
        setSharingSettings(data.socialSharingSettings || sharingSettings);
      }
    } catch (error) {
      console.error('Error loading social media data:', error);
    }
  };

  const handleConnectPlatform = async (platformId: string) => {
    setLoading(true);
    
    try {
      // Simulate OAuth flow (in real implementation, this would redirect to OAuth)
      const mockConnection: SocialMediaConnection = {
        platform: platformId,
        connected: true,
        username: `@user_${platformId}`,
        accessToken: `mock_token_${Date.now()}`,
        lastSync: new Date()
      };

      const updatedConnections = connections.filter(c => c.platform !== platformId);
      updatedConnections.push(mockConnection);
      setConnections(updatedConnections);

      // Update Firebase
      const profileRef = doc(db, 'userProfiles', uid);
      await updateDoc(profileRef, {
        socialMediaConnections: updatedConnections,
        updatedAt: new Date()
      });

      alert(`${SOCIAL_PLATFORMS.find(p => p.id === platformId)?.name} succesvol gekoppeld!`);
    } catch (error) {
      console.error('Error connecting platform:', error);
      alert('Er is een fout opgetreden bij het koppelen. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectPlatform = async (platformId: string) => {
    const updatedConnections = connections.filter(c => c.platform !== platformId);
    setConnections(updatedConnections);

    try {
      const profileRef = doc(db, 'userProfiles', uid);
      await updateDoc(profileRef, {
        socialMediaConnections: updatedConnections,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error disconnecting platform:', error);
    }
  };

  const handleSharingSettingsChange = (updates: Partial<SocialSharingSettings>) => {
    const newSettings = { ...sharingSettings, ...updates };
    setSharingSettings(newSettings);
  };

  const handleSaveAndContinue = async () => {
    setLoading(true);
    
    try {
      const profileRef = doc(db, 'userProfiles', uid);
      await updateDoc(profileRef, {
        socialMediaConnections: connections,
        socialSharingSettings: sharingSettings,
        updatedAt: new Date()
      });
      
      onNext();
    } catch (error) {
      console.error('Error saving social media settings:', error);
      alert('Er is een fout opgetreden bij het opslaan.');
    } finally {
      setLoading(false);
    }
  };

  const isConnected = (platformId: string) => {
    return connections.some(c => c.platform === platformId && c.connected);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Social Media Koppelingen</h2>
        <p className="text-gray-600">
          Koppel je social media accounts voor automatische content sharing en betere zichtbaarheid.
        </p>
      </div>

      {/* Social Media Platforms */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Beschikbare Platforms</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SOCIAL_PLATFORMS.map((platform) => {
            const connected = isConnected(platform.id);
            const connection = connections.find(c => c.platform === platform.id);
            
            return (
              <div
                key={platform.id}
                className={`p-4 border rounded-lg ${
                  connected ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <span>{platform.icon}</span>
                    <div>
                      <h4 className="font-medium text-gray-900">{platform.name}</h4>
                      <p className="text-sm text-gray-600">{platform.description}</p>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    connected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {connected ? 'Gekoppeld' : 'Niet gekoppeld'}
                  </div>
                </div>

                {/* Benefits */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-1">Voordelen:</p>
                  <div className="flex flex-wrap gap-1">
                    {platform.benefits.map((benefit, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                      >
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Connection Status */}
                {connected && connection && (
                  <div className="mb-4 p-2 bg-green-100 rounded text-sm">
                    <p className="text-green-800">
                      <strong>Gekoppeld als:</strong> {connection.username}
                    </p>
                    {connection.lastSync && (
                      <p className="text-green-600 text-xs">
                        Laatste sync: {connection.lastSync.toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Action Button */}
                <button
                  onClick={() => 
                    connected 
                      ? handleDisconnectPlatform(platform.id)
                      : handleConnectPlatform(platform.id)
                  }
                  disabled={loading}
                  className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                    connected
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : `${platform.color} text-white hover:opacity-90`
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? 'Bezig...' : connected ? 'Ontkoppelen' : 'Koppelen'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Social Sharing Settings */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Automatische Sharing Instellingen</h3>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          {/* Enable/Disable Sharing */}
          <div className="mb-6">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={sharingSettings.enabled}
                onChange={(e) => handleSharingSettingsChange({ enabled: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-blue-800">
                Automatische content sharing inschakelen
              </span>
            </label>
            <p className="text-blue-600 text-sm mt-1 ml-7">
              Automatisch je podcast deelnames en content delen op gekoppelde platforms.
            </p>
          </div>

          {sharingSettings.enabled && (
            <>
              {/* Sharing Frequency */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-blue-800 mb-3">
                  Sharing frequentie
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'off', label: 'Uit', desc: 'Geen automatische shares' },
                    { value: '3x', label: '3x per week', desc: 'Gemiddelde frequentie' },
                    { value: '5x', label: '5x per week', desc: 'Actieve frequentie' },
                    { value: '7x', label: 'Dagelijks', desc: 'Maximale zichtbaarheid' }
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex flex-col p-3 border rounded-lg cursor-pointer transition-colors ${
                        sharingSettings.frequency === option.value
                          ? 'border-blue-400 bg-blue-100'
                          : 'border-blue-200 hover:bg-blue-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="frequency"
                        value={option.value}
                        checked={sharingSettings.frequency === option.value}
                        onChange={(e) => handleSharingSettingsChange({ frequency: e.target.value as any })}
                        className="sr-only"
                      />
                      <span className="font-medium text-blue-800">{option.label}</span>
                      <span className="text-xs text-blue-600">{option.desc}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Platform Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-blue-800 mb-3">
                  Platforms voor automatische sharing
                </label>
                <div className="space-y-2">
                  {SOCIAL_PLATFORMS.map((platform) => {
                    const connected = isConnected(platform.id);
                    return (
                      <label
                        key={platform.id}
                        className={`flex items-center space-x-3 p-3 border rounded-lg ${
                          connected ? 'border-blue-200 bg-white' : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={sharingSettings.platforms.includes(platform.id)}
                          onChange={(e) => {
                            const platforms = e.target.checked
                              ? [...sharingSettings.platforms, platform.id]
                              : sharingSettings.platforms.filter(p => p !== platform.id);
                            handleSharingSettingsChange({ platforms });
                          }}
                          disabled={!connected}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span>{platform.icon}</span>
                        <div className="flex-1">
                          <span className={`font-medium ${
                            connected ? 'text-gray-900' : 'text-gray-500'
                          }`}>
                            {platform.name}
                          </span>
                          {!connected && (
                            <span className="text-xs text-gray-500 ml-2">(Eerst koppelen)</span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Content Templates */}
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-3">
                  Content templates (optioneel)
                </label>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-blue-700 mb-1">LinkedIn template</label>
                    <textarea
                      value={sharingSettings.templates.linkedin || ''}
                      onChange={(e) => handleSharingSettingsChange({
                        templates: { ...sharingSettings.templates, linkedin: e.target.value }
                      })}
                      placeholder="🎙️ Net verschenen op [PODCAST_NAME] - [TOPIC]..."
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-blue-700 mb-1">Twitter template</label>
                    <textarea
                      value={sharingSettings.templates.twitter || ''}
                      onChange={(e) => handleSharingSettingsChange({
                        templates: { ...sharingSettings.templates, twitter: e.target.value }
                      })}
                      placeholder="🎧 Net geluisterd naar [PODCAST_NAME] over [TOPIC]..."
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <button
          onClick={onBack}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          ← Terug
        </button>
        <button
          onClick={handleSaveAndContinue}
          disabled={loading}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {loading ? 'Opslaan...' : 'Opslaan & Doorgaan →'}
        </button>
      </div>
    </div>
  );
}
